/**
 * Field Binding Registry
 *
 * Static mapping of form field names → Effector store paths.
 *
 * All address and dwelling fields use origin/destination prefixes so they
 * can be placed on any step without requiring step-specific keys.
 *
 * Key format:
 *   "stepId::fieldName"  — step-specific binding (checked first)
 *   "fieldName"           — global default binding (fallback)
 *
 * This decouples field identity from page identity, allowing fields
 * to be moved between steps via CMS without breaking store persistence.
 */
import { AccountType, AccountTypes } from "@hunkware/shared/types";
import type { StoreName } from "./store-accessors";

export type FieldBinding = {
  store: StoreName;
  /** Dot-notated path within the store's inner object */
  path: string;
  /** Optional transform applied before writing to the store */
  transform?: (value: unknown, formData: Record<string, unknown>) => unknown;
  /** Optional reverse transform applied when reading from the store back to form value */
  readTransform?: (storeValue: unknown) => unknown;
  /** Optional: the method on the store's API to call instead of generic `set` */
  apiMethod?: string;
};

export type FieldBindingMap = Record<string, FieldBinding>;

/**
 * The master registry of all field → store bindings.
 *
 * Step-specific keys use the format "stepId::fieldName".
 * Global keys use just "fieldName".
 */
export const fieldBindingRegistry: FieldBindingMap = {
  // ──────────────────────────────────────────────
  // get-started
  // ──────────────────────────────────────────────
  "get-started::services": {
    store: "bookingProcess",
    path: "selectedServices",
    apiMethod: "setSelectedServices",
  },

  // ──────────────────────────────────────────────
  // origin address (pick-up-location)  →  account.billingAddress.*
  // Field name in form: "origin" (type: address)
  // Sub-fields: origin, origin2, originCity, originState, originPostal, originCountry
  // ──────────────────────────────────────────────
  origin: { store: "account", path: "billingAddress.address" },
  origin2: { store: "account", path: "billingAddress.address2" },
  originCity: { store: "account", path: "billingAddress.city" },
  originState: { store: "account", path: "billingAddress.state" },
  originPostal: { store: "account", path: "billingAddress.postal" },
  originCountry: { store: "account", path: "billingAddress.country" },

  forBusiness: {
    store: "account",
    path: "type",
    transform: (value: unknown) =>
      value
        ? {
            id: AccountType.Commercial,
            name: AccountTypes[AccountType.Commercial],
          }
        : {
            id: AccountType.Residential,
            name: AccountTypes[AccountType.Residential],
          },
    readTransform: (storeValue: unknown) => {
      if (typeof storeValue === "object" && storeValue !== null) {
        return (storeValue as { id: number }).id === AccountType.Commercial;
      }
      return false;
    },
  },

  // ──────────────────────────────────────────────
  // contact-information  →  account.*
  // ──────────────────────────────────────────────
  firstName: { store: "account", path: "firstName" },
  lastName: { store: "account", path: "lastName" },
  phone: { store: "account", path: "phone" },
  email: { store: "account", path: "email" },
  emailSubscribe: {
    store: "account",
    path: "marketing.offersOptIn",
    transform: (value: unknown) => !!value,
  },
  // Free-text promo code (CID-7209) is intentionally NOT auto-bound to the store.
  // The promo-code field renderer writes account.requestedPromoCode itself, and
  // only when the code should be saved (valid, or skip-validation mode) — so an
  // invalid/unvalidated code is never persisted or carried across service changes.

  // ──────────────────────────────────────────────
  // kind-of-items  →  appointment.notes.typeOfJunk
  // ──────────────────────────────────────────────
  typeOfJunk: { store: "appointment", path: "notes.typeOfJunk" },

  // ──────────────────────────────────────────────
  // target-date  →  quote.date
  // ──────────────────────────────────────────────
  targetDate: { store: "quote", path: "date" },

  // ──────────────────────────────────────────────
  // origin dwelling (dwelling-information)  →  quote.origin.*
  // ──────────────────────────────────────────────
  originTypeOfDwelling: {
    store: "quote",
    path: "origin.dwellingType.id",
    // Store as a number: Sanity option values are strings ("2"), but every
    // consumer (QuoteTypeOfDwellingEnum comparisons, promo validation,
    // quote payload) treats the id as numeric — same as the PHP-echoed
    // quote that replaces this store after quoteCreate.
    transform: (value: unknown) => {
      const raw =
        typeof value === "object" && value !== null
          ? (value as any).value
          : value;
      const parsed = parseInt(String(raw), 10);
      return isNaN(parsed) ? raw : parsed;
    },
    readTransform: (v: unknown) => (v != null ? String(v) : undefined),
  },
  originFloor: {
    store: "quote",
    path: "origin.floor",
    transform: (value: unknown) => {
      const raw =
        typeof value === "object" && value !== null
          ? (value as any).value
          : value;
      // Store as integer (1-based); converted to 0-based in quoteCreate payload
      const parsed = parseInt(String(raw), 10);
      return isNaN(parsed) ? raw : parsed;
    },
    readTransform: (v: unknown) => (v != null ? String(v) : undefined),
  },
  originAccessType: {
    store: "quote",
    path: "origin.accessType",
    transform: (value: unknown, formData: Record<string, unknown>) => {
      // Sentinel means field was hidden (floor ≤ 1) — treat as ground floor default
      if (value === "__none__") return 1;
      const floor = formData.originFloor;
      const floorVal =
        typeof floor === "object" && floor !== null
          ? (floor as any).value
          : floor;
      if (!floorVal) return 1; // ground floor defaults to 1
      const raw =
        typeof value === "object" && value !== null
          ? (value as any).value
          : value;
      const parsed = parseInt(String(raw), 10);
      return isNaN(parsed) ? raw : parsed;
    },
    readTransform: (v: unknown) => (v != null ? String(v) : undefined),
  },
  originLongWalks: {
    store: "quote",
    path: "origin.longWalks",
    transform: (value: unknown) =>
      typeof value === "object" && value !== null
        ? (value as any).value
        : value,
    readTransform: (v: unknown) => (v != null ? String(v) : undefined),
  },

  // ──────────────────────────────────────────────
  // destination address (destination-location)  →  quote.destination.*
  // Field name in form: "destination" (type: address)
  // Sub-fields: destination, destination2, destinationCity, destinationState, destinationPostal, destinationCountry
  // ──────────────────────────────────────────────
  destination: { store: "quote", path: "destination.address" },
  destination2: { store: "quote", path: "destination.address2" },
  destinationCity: { store: "quote", path: "destination.city" },
  destinationState: { store: "quote", path: "destination.state" },
  destinationPostal: { store: "quote", path: "destination.postal" },
  destinationCountry: { store: "quote", path: "destination.country" },

  // ──────────────────────────────────────────────
  // destination dwelling (destination-dwelling-information)  →  quote.destination.*
  // ──────────────────────────────────────────────
  destinationTypeOfDwelling: {
    store: "quote",
    path: "destination.dwellingType.id",
    // Same number-in-store convention as originTypeOfDwelling above.
    transform: (value: unknown) => {
      const raw =
        typeof value === "object" && value !== null
          ? (value as any).value
          : value;
      const parsed = parseInt(String(raw), 10);
      return isNaN(parsed) ? raw : parsed;
    },
    readTransform: (v: unknown) => (v != null ? String(v) : undefined),
  },
  destinationFloor: {
    store: "quote",
    path: "destination.floor",
    transform: (value: unknown) => {
      const raw =
        typeof value === "object" && value !== null
          ? (value as any).value
          : value;
      // Store as integer (1-based); converted to 0-based in quoteCreate payload
      const parsed = parseInt(String(raw), 10);
      return isNaN(parsed) ? raw : parsed;
    },
    readTransform: (v: unknown) => (v != null ? String(v) : undefined),
  },
  destinationAccessType: {
    store: "quote",
    path: "destination.accessType",
    transform: (value: unknown, formData: Record<string, unknown>) => {
      // Sentinel means field was hidden (floor ≤ 1) — treat as ground floor default
      if (value === "__none__") return 1;
      const floor = formData.destinationFloor;
      const floorVal =
        typeof floor === "object" && floor !== null
          ? (floor as any).value
          : floor;
      if (!floorVal) return 1;
      const raw =
        typeof value === "object" && value !== null
          ? (value as any).value
          : value;
      const parsed = parseInt(String(raw), 10);
      return isNaN(parsed) ? raw : parsed;
    },
    readTransform: (v: unknown) => (v != null ? String(v) : undefined),
  },
  destinationLongWalks: {
    store: "quote",
    path: "destination.longWalks",
    transform: (value: unknown) =>
      typeof value === "object" && value !== null
        ? (value as any).value
        : value,
    readTransform: (v: unknown) => (v != null ? String(v) : undefined),
  },

  // ──────────────────────────────────────────────
  // number-bedrooms  →  quote.rooms
  // ──────────────────────────────────────────────
  numberBedroom: { store: "quote", path: "rooms" },

  // ──────────────────────────────────────────────
  // provide-more-details  →  bookingProcess.bookingType
  // ──────────────────────────────────────────────
  bookingType: {
    store: "bookingProcess",
    path: "bookingType",
    apiMethod: "setBookingType",
  },

  // ──────────────────────────────────────────────
  // additional-factors  →  quote.*
  // ──────────────────────────────────────────────
  appliances: { store: "quote", path: "appliances" },
  heavyItems: { store: "quote", path: "heavyItems" },
  assemblyItems: { store: "quote", path: "assemblyItems" },

  // ──────────────────────────────────────────────
  // truck-loads  →  appointment.bookingConfirmed
  //
  // Hidden field — only synced on explicit submit (excluded from debounce).
  // When this field's value reaches the store, the appointmentCreate contract
  // fires (assuming all other prerequisites are already met).
  // CMS editors can move this hidden field to any step to change when
  // the booking fires — or remove it to prevent automatic booking entirely.
  // ──────────────────────────────────────────────
  bookingConfirmed: { store: "appointment", path: "bookingConfirmed" },

  // ──────────────────────────────────────────────
  // any step  →  appointment.callRequestConfirmed
  //
  // Hidden field — only synced on explicit submit (excluded from debounce).
  // When this field's value reaches the store, the callRequest contract
  // fires (assuming account.id and serviceLocation.id are already set).
  // CMS editors can place this hidden field on any step of any service
  // category to unconditionally trigger a call request at that point.
  // ──────────────────────────────────────────────
  callRequestConfirmed: { store: "appointment", path: "callRequestConfirmed" },

  // ──────────────────────────────────────────────
  // contact-information  →  appointment.contactConfirmed
  //
  // Hidden field — gating prerequisite for accountCreate on the Labor flow.
  // Without this, accountCreate can fire at pick-up-location if contact info
  // persists from a prior Moving flow (all prerequisites already satisfied
  // the moment serviceCheck resolves). By requiring this hidden field (set only
  // when the user submits contact-information), accountCreate is deferred until
  // the user actually fills out and submits the contact form.
  // ──────────────────────────────────────────────
  "contact-information::contactConfirmed": {
    store: "appointment",
    path: "contactConfirmed",
  },
};

/**
 * Resolves a field binding for a given step + field name.
 * Checks step-specific key first, falls back to global key.
 */
export const resolveBinding = (
  stepId: string,
  fieldName: string,
): FieldBinding | undefined => {
  return (
    fieldBindingRegistry[`${stepId}::${fieldName}`] ??
    fieldBindingRegistry[fieldName]
  );
};
